import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { pxToRem } from '../../../assets/styles/helper';
import Modal from '../../Modal/Modal';
import { SFH_WIZARD } from '../../../constants/sessionStorage';
import {
  closeWizardContent,
  getWizardContent,
  setWizardContent,
} from '../../../util/helpers/wizardHelpers';
import SellFromHomeProductSelection from './SellFromHomeProductSelection';
import SellFromHomeQuantity from './SellFromHomeQuantity';
import SellFromHomeShipping from './SellFromHomeShipping';
import SellFromHomeReview from './SellFromHomeReview';
import SellFromHomeConfirmation from './SellFromHomeConfirmation';
import SellFromHomeTransactionDetails from './SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from './SellFromHomeTransactionHistory';
import SellFromHomeStepsDrawer from './SellFromHomeStepsDrawer';

const PRODUCT_SELECTION_STEP = 0;
const QUANTITY_STEP = 1;
const SHIPPING_STEP = 2;
const REVIEW_STEP = 3;
const CONFIRMATION_STEP = 4;
const TRANSACTION_DETAILS_STEP = 5;
const TRANSACTION_HISTORY_STEP = 6;

const STEP_TITLE_KEYS = {
  [PRODUCT_SELECTION_STEP]: 'sellWizard.sfhWizard.title',
  [QUANTITY_STEP]: 'sellWizard.sfhWizard.title',
  [SHIPPING_STEP]: 'sellWizard.sfhWizard.title',
  [REVIEW_STEP]: 'sellWizard.sfhWizard.review.title',
  [CONFIRMATION_STEP]: null,
  [TRANSACTION_DETAILS_STEP]: 'sellWizard.sfhWizard.transactionDetails.title',
  [TRANSACTION_HISTORY_STEP]: 'sellWizard.sfhWizard.transactionHistory.title',
};

const SellFromHomeWizard = ({ isOpen, onClose, fromSellWizard }) => {
  const { t } = useTranslation();
  const [isStepsDrawerOpen, setIsStepsDrawerOpen] = useState(false);
  const [selectedHit, setSelectedHit] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [lockedSnapshot, setLockedSnapshot] = useState(null);
  const [currentStep, setCurrentStep] = useState(
    () => Number(getWizardContent(SFH_WIZARD)) || PRODUCT_SELECTION_STEP,
  );

  useEffect(() => {
    setIsStepsDrawerOpen(isOpen);
    if (!isOpen) {
      setSelectedHit(null);
      setQuantity(0);
      setSelectedPaymentMethod(null);
      setLockedSnapshot(null);
      setCurrentStep(PRODUCT_SELECTION_STEP);
      closeWizardContent();
    }
  }, [isOpen]);

  const goToStep = (step) => {
    setWizardContent(SFH_WIZARD, step);
    setCurrentStep(step);
  };

  const goStepBack = () => goToStep(Math.max(currentStep - 1, 0));
  const goStepForward = () =>
    goToStep(Math.min(currentStep + 1, TRANSACTION_HISTORY_STEP));

  const handleClose = () => {
    closeWizardContent();
    setSelectedHit(null);
    setQuantity(0);
    setSelectedPaymentMethod(null);
    setLockedSnapshot(null);
    setCurrentStep(PRODUCT_SELECTION_STEP);
    onClose();
  };

  const handleHeaderBack = () => {
    if (currentStep === PRODUCT_SELECTION_STEP) {
      handleClose();
      return;
    }
    goStepBack();
  };

  const handleSubmitOrder = (payload) => {
    setLockedSnapshot(payload);
    goToStep(CONFIRMATION_STEP);
  };

  const handleViewOrder = () => goToStep(TRANSACTION_DETAILS_STEP);
  const handleViewHistory = () => goToStep(TRANSACTION_HISTORY_STEP);

  const showHeaderBack = (() => {
    if (
      currentStep === CONFIRMATION_STEP ||
      currentStep === TRANSACTION_DETAILS_STEP
    )
      return false;
    if (currentStep === PRODUCT_SELECTION_STEP) return Boolean(fromSellWizard);
    return true;
  })();

  const titleKey = STEP_TITLE_KEYS[currentStep];

  return (
    <Modal
      isOpen={isOpen}
      close={handleClose}
      goBack={showHeaderBack ? handleHeaderBack : undefined}
      title={titleKey ? t(titleKey) : ''}
      size="sm"
      maxWidth={pxToRem(600)}
      minHeight={pxToRem(620)}
      clipContent
      closeIconVisible
      hideHeader={currentStep === CONFIRMATION_STEP}
      skipListeningToLocations
    >
      {currentStep === PRODUCT_SELECTION_STEP && (
        <SellFromHomeProductSelection
          selectedHit={selectedHit}
          setSelectedHit={setSelectedHit}
          handleNext={goStepForward}
        />
      )}
      {currentStep === QUANTITY_STEP && (
        <SellFromHomeQuantity
          selectedHit={selectedHit}
          quantity={quantity}
          setQuantity={setQuantity}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          handleBack={goStepBack}
          handleNext={goStepForward}
          handleClose={handleClose}
        />
      )}
      {currentStep === SHIPPING_STEP && (
        <SellFromHomeShipping
          selectedHit={selectedHit}
          handleBack={goStepBack}
          handleNext={goStepForward}
          handleClose={handleClose}
        />
      )}
      {currentStep === REVIEW_STEP && (
        <SellFromHomeReview
          selectedHit={selectedHit}
          quantity={quantity}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          handleSubmitOrder={handleSubmitOrder}
          handleClose={handleClose}
        />
      )}
      {currentStep === CONFIRMATION_STEP && (
        <SellFromHomeConfirmation
          selectedHit={selectedHit}
          quantity={quantity}
          lockedUnitPrice={lockedSnapshot?.unitPrice ?? 0}
          lockedPayout={lockedSnapshot?.payout ?? 0}
          handleClose={handleClose}
          handleViewOrder={handleViewOrder}
        />
      )}
      {currentStep === TRANSACTION_DETAILS_STEP && (
        <SellFromHomeTransactionDetails
          selectedHit={selectedHit}
          quantity={quantity}
          lockedSnapshot={lockedSnapshot}
          handleClose={handleClose}
          handleViewHistory={handleViewHistory}
        />
      )}
      {currentStep === TRANSACTION_HISTORY_STEP && (
        <SellFromHomeTransactionHistory />
      )}
      <SellFromHomeStepsDrawer
        isOpen={isStepsDrawerOpen}
        onClose={() => setIsStepsDrawerOpen(false)}
      />
    </Modal>
  );
};

SellFromHomeWizard.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fromSellWizard: PropTypes.bool,
};

export default SellFromHomeWizard;
